import React from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { FileText, Eye, BookOpen, AlignLeft, Layers } from "lucide-react";

export interface ExampleDetailModalExample {
  name: string;
  activityName?: string;
  description?: string;
  prologue?: string;
  epilogue?: string;
}

interface ExampleDetailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  example: ExampleDetailModalExample | null;
  onViewClick: () => void;
}

const ExampleDetailModal = ({
  open,
  onOpenChange,
  example,
  onViewClick,
}: ExampleDetailModalProps) => {
  const { t } = useTranslation();

  if (!example) return null;

  const descriptionHtml =
    typeof example.description === "string" && example.description
      ? example.description
      : "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <FileText className="w-5 h-5 text-primary" />
            {t("example_details") || "Example Details"}
          </DialogTitle>
          <DialogDescription>
            {t("view_example_information") ||
              "View example information and content"}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          {/* Name Section */}
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 flex-shrink-0 mt-0.5">
              <FileText className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="mb-1 text-sm font-medium text-gray-500">
                {t("name") || "Name"}
              </p>
              <p className="text-base font-semibold text-gray-900">
                {example.name}
              </p>
            </div>
          </div>

          {/* Activity Section */}
          {example.activityName && (
            <div className="flex items-start gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-indigo-50 flex-shrink-0 mt-0.5">
                <Layers className="w-5 h-5 text-indigo-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="mb-2 text-sm font-medium text-gray-500">
                  {t("activity") || "Activity"}
                </p>
                <p className="text-base font-semibold text-gray-900">
                  {example.activityName}
                </p>
              </div>
            </div>
          )}

          {/* Prologue Section */}
          {example.prologue && (
            <div className="flex items-start gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-purple-50 flex-shrink-0 mt-0.5">
                <BookOpen className="w-5 h-5 text-purple-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="mb-2 text-sm font-medium text-gray-500">
                  {t("prologue") || "Prologue"}
                </p>
                <div
                  className="text-sm leading-relaxed prose-sm prose text-gray-700 max-w-none"
                  dangerouslySetInnerHTML={{ __html: example.prologue }}
                />
              </div>
            </div>
          )}

          {/* Description Section */}
          {descriptionHtml && (
            <div className="flex items-start gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/5 flex-shrink-0 mt-0.5">
                <AlignLeft className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="mb-2 text-sm font-medium text-gray-500">
                  {t("description") || "Description"}
                </p>
                <div
                  className="text-sm leading-relaxed prose-sm prose text-gray-700 max-w-none"
                  dangerouslySetInnerHTML={{ __html: descriptionHtml }}
                />
              </div>
            </div>
          )}

          {/* Epilogue Section */}
          {example.epilogue && (
            <div className="flex items-start gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-green-50 flex-shrink-0 mt-0.5">
                <BookOpen className="w-5 h-5 text-green-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="mb-2 text-sm font-medium text-gray-500">
                  {t("epilogue") || "Epilogue"}
                </p>
                <div
                  className="text-sm leading-relaxed prose-sm prose text-gray-700 max-w-none"
                  dangerouslySetInnerHTML={{ __html: example.epilogue }}
                />
              </div>
            </div>
          )}

          {/* Empty state if no additional info */}
          {!descriptionHtml && !example.prologue && !example.epilogue && (
            <div className="py-4 text-sm text-center text-gray-500">
              {t("no_additional_info") || "No additional information available"}
            </div>
          )}
        </div>

        <div className="flex justify-between gap-3 pt-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("close") || "Close"}
          </Button>
          <Button onClick={onViewClick} className="gap-1.5">
            <Eye className="w-4 h-4" />
            {t("view") || "View"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ExampleDetailModal;
